import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FileText, Download, Sparkle } from '@phosphor-icons/react';
import jsPDF from 'jspdf';

const Reports = () => {
  const [profile, setProfile] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [profileRes, statsRes, expensesRes, accountsRes] = await Promise.all([
        api.get('/financial-profile'),
        api.get('/dashboard/stats'),
        api.get('/expenses'),
        api.get('/retirement-accounts')
      ]);
      
      setProfile(profileRes.data);
      setDashboardStats(statsRes.data);
      setExpenses(expensesRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateFinancialSummary = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(10, 17, 40);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(197, 160, 89);
    doc.setFontSize(22);
    doc.text('PLANIVANCE AI', 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(248, 249, 250);
    doc.text('Financial Summary Report', 105, 30, { align: 'center' });
    
    // Reset colors
    doc.setTextColor(26, 32, 44);
    
    // Date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 50);
    
    // Dashboard Stats
    doc.setFontSize(16);
    doc.text('Financial Overview', 20, 65);
    doc.setFontSize(11);
    doc.text(`Monthly Income: $${dashboardStats?.monthly_income?.toLocaleString() || 0}`, 20, 75);
    doc.text(`Monthly Expenses: $${dashboardStats?.monthly_expenses?.toLocaleString() || 0}`, 20, 82);
    doc.text(`Net Cash Flow: $${dashboardStats?.net_cash_flow?.toLocaleString() || 0}`, 20, 89);
    doc.text(`Savings Rate: ${dashboardStats?.savings_rate?.toFixed(1) || 0}%`, 20, 96);
    doc.text(`Financial Health Score: ${dashboardStats?.financial_health_score || 0}/100`, 20, 103);
    
    // Retirement Accounts
    const totalRetirement = accounts.reduce((sum, acc) => sum + acc.balance, 0);
    doc.setFontSize(16);
    doc.text('Retirement Accounts', 20, 120);
    doc.setFontSize(11);
    doc.text(`Total Balance: $${totalRetirement.toLocaleString()}`, 20, 130);
    doc.text(`Number of Accounts: ${accounts.length}`, 20, 137);
    
    // Recent Expenses
    const totalExpenses = expenses.slice(0, 10).reduce((sum, e) => sum + e.amount, 0);
    doc.setFontSize(16);
    doc.text('Recent Expenses', 20, 155);
    doc.setFontSize(11);
    doc.text(`Total (Last 10): $${totalExpenses.toLocaleString()}`, 20, 165);
    
    // Disclaimers
    doc.setFontSize(8);
    doc.setTextColor(74, 85, 104);
    doc.text('IMPORTANT DISCLAIMERS:', 20, 250);
    doc.text('This report is for educational purposes only and does not constitute financial, investment, tax, or legal advice.', 20, 256);
    doc.text('Consult qualified professionals before making financial decisions. Past performance does not guarantee future results.', 20, 261);
    
    // Footer
    doc.setFontSize(9);
    doc.text('PLANIVANCE AI - A Product of Outsource Solution Strategies, LLC', 105, 285, { align: 'center' });
    
    doc.save('planivance-financial-summary.pdf');
    toast.success('Report downloaded successfully!');
  };

  const generateRetirementReadiness = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(10, 17, 40);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(197, 160, 89);
    doc.setFontSize(22);
    doc.text('PLANIVANCE AI', 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(248, 249, 250);
    doc.text('Retirement Readiness Report', 105, 30, { align: 'center' });
    
    doc.setTextColor(26, 32, 44);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 50);
    
    // Profile Info
    doc.setFontSize(16);
    doc.text('Your Profile', 20, 65);
    doc.setFontSize(11);
    doc.text(`Current Age: ${profile?.age || 'N/A'}`, 20, 75);
    doc.text(`Target Retirement Age: ${profile?.retirement_age || 'N/A'}`, 20, 82);
    doc.text(`Years to Retirement: ${(profile?.retirement_age || 65) - (profile?.age || 30)}`, 20, 89);
    doc.text(`Life Expectancy: ${profile?.life_expectancy || 90}`, 20, 96);
    
    // Retirement Assets
    const totalRetirement = accounts.reduce((sum, acc) => sum + acc.balance, 0);
    const annualContributions = accounts.reduce((sum, acc) => sum + acc.annual_contribution, 0);
    
    doc.setFontSize(16);
    doc.text('Retirement Assets', 20, 115);
    doc.setFontSize(11);
    doc.text(`Current Balance: $${totalRetirement.toLocaleString()}`, 20, 125);
    doc.text(`Annual Contributions: $${annualContributions.toLocaleString()}`, 20, 132);
    
    // Projected Growth (simple 7% annual)
    const yearsToRetirement = (profile?.retirement_age || 65) - (profile?.age || 30);
    const projectedBalance = totalRetirement * Math.pow(1.07, yearsToRetirement) + 
                             annualContributions * ((Math.pow(1.07, yearsToRetirement) - 1) / 0.07);
    
    doc.setFontSize(16);
    doc.text('Projections', 20, 150);
    doc.setFontSize(11);
    doc.text(`Projected Balance at Retirement (7% growth): $${Math.round(projectedBalance).toLocaleString()}`, 20, 160);
    
    // Recommendations
    doc.setFontSize(16);
    doc.text('Educational Considerations', 20, 180);
    doc.setFontSize(10);
    doc.text('• Review and maximize retirement account contributions', 20, 190);
    doc.text('• Consider tax diversification (Traditional vs Roth)', 20, 197);
    doc.text('• Evaluate Social Security claiming strategies', 20, 204);
    doc.text('• Plan for healthcare costs in retirement', 20, 211);
    doc.text('• Consult with a qualified financial advisor for personalized guidance', 20, 218);
    
    // Disclaimers
    doc.setFontSize(8);
    doc.setTextColor(74, 85, 104);
    doc.text('IMPORTANT DISCLAIMERS:', 20, 250);
    doc.text('Projections are educational estimates only and not guarantees. Actual results will vary based on market conditions,', 20, 256);
    doc.text('contributions, and other factors. This report does not constitute financial advice. Consult professionals for personalized planning.', 20, 261);
    
    // Footer
    doc.setFontSize(9);
    doc.text('PLANIVANCE AI - A Product of Outsource Solution Strategies, LLC', 105, 285, { align: 'center' });
    
    doc.save('planivance-retirement-readiness.pdf');
    toast.success('Report downloaded successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#4A5568]">Loading report data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Reports:</strong> These reports summarize your data for educational purposes. They do not constitute financial advice. Consult qualified professionals for personalized guidance.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Reports
        </h1>
        <p className="text-base text-[#4A5568]">
          Generate downloadable PDF reports of your financial data
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="financial-summary-report">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-[#0A1128]/10 rounded-md">
              <FileText size={32} weight="duotone" className="text-[#0A1128]" />
            </div>
            <div className="flex-1">
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-2">Financial Summary</h2>
              <p className="text-sm text-[#4A5568]">
                Current financial snapshot including income, expenses, and accounts
              </p>
            </div>
          </div>
          
          <ul className="space-y-2 mb-6 text-sm text-[#4A5568]">
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Dashboard metrics
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Retirement account balances
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Recent expense summary
            </li>
          </ul>
          
          <Button onClick={generateFinancialSummary} data-testid="generate-summary-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
            <Download size={18} className="mr-2" />
            Generate Summary Report
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="retirement-readiness-report">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-[#C5A059]/10 rounded-md">
              <FileText size={32} weight="duotone" className="text-[#C5A059]" />
            </div>
            <div className="flex-1">
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-2">Retirement Readiness</h2>
              <p className="text-sm text-[#4A5568]">
                Assessment of retirement preparedness with projections
              </p>
            </div>
          </div>
          
          <ul className="space-y-2 mb-6 text-sm text-[#4A5568]">
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Retirement timeline
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Asset projections
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[#C5A059]">•</span> Planning considerations
            </li>
          </ul>
          
          <Button onClick={generateRetirementReadiness} data-testid="generate-readiness-button" className="w-full bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A1128]">
            <Download size={18} className="mr-2" />
            Generate Readiness Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
